import { z } from "zod";
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const text = z.string().max(2_000);
const identifier = z.string().min(1).max(160);
const date = z.string().date();
const nullableText = text.nullable();
const decimal = z
    .string()
    .regex(/^-?\d+(?:\.\d{1,18})?$/)
    .max(100)
    .nullable();
export const PRODUCT_CATALOG_LIMITS = {
    maxRows: 50_000,
    maxBatchRows: 100,
    maxPreparedBytes: 128 * 1024 * 1024,
};
export const productCatalogIssueSchema = z.enum([
    "MISSING_SKU",
    "MISSING_NAME",
    "IDENTIFIER_FORMAT_UNKNOWN",
    "NUMERIC_IDENTIFIER_UNSAFE",
    "INVALID_NCM",
    "NCM_EXTENSION_PRESENT",
    "INVALID_CEST",
    "INVALID_GTIN",
    "GTIN_SHARED",
    "SKU_REPEATED",
    "FORMULA_NOT_EVALUATED",
    "PRICE_UNPARSEABLE",
    "STOCK_UNPARSEABLE",
]);
export const productCatalogCellSchema = z
    .object({
    cell: z.string().regex(/^[A-Z]{1,3}[1-9]\d{0,6}$/),
    kind: z.enum(["STRING", "NUMBER", "BOOLEAN", "BLANK", "FORMULA", "ERROR"]),
    numberFormat: z.string().max(160).nullable(),
})
    .strict();
export const productCatalogDeclaredSchema = z
    .object({
    status: nullableText,
    sku: identifier.nullable(),
    name: nullableText,
    family: nullableText,
    brand: nullableText,
    model: nullableText,
    gtinRaw: nullableText,
    gtin: z
        .string()
        .regex(/^\d{8,14}$/)
        .nullable(),
    ncmRaw: nullableText,
    ncm: z
        .string()
        .regex(/^\d{8}$/)
        .nullable(),
    ncmExtension: z.string().max(160).nullable(),
    cestRaw: nullableText,
    cest: z
        .string()
        .regex(/^\d{2}\.\d{3}\.\d{2}$/)
        .nullable(),
    unit: nullableText,
    unitPriceRaw: nullableText,
    unitPrice: decimal,
    availableStockRaw: nullableText,
    availableStock: decimal,
    netWeightRaw: nullableText,
    grossWeightRaw: nullableText,
    heightRaw: nullableText,
    widthRaw: nullableText,
    depthRaw: nullableText,
    goodsOriginRaw: nullableText,
    characteristics: nullableText,
    includedAtRaw: nullableText,
    updatedAtRaw: nullableText,
})
    .strict();
export const productCatalogPreparedRowSchema = z
    .object({
    sourceRow: z.number().int().min(1).max(1_048_576),
    sourceRowSha256: hash,
    declared: productCatalogDeclaredSchema,
    cells: z.record(z.string().min(1).max(60), productCatalogCellSchema),
    issues: z.array(productCatalogIssueSchema).max(20),
})
    .strict()
    .superRefine((value, ctx) => {
    const derived = new Set(["gtin", "ncm", "ncmExtension", "cest", "unitPrice", "availableStock"]);
    for (const field of Object.keys(value.declared)) {
        if (!derived.has(field) && !value.cells[field])
            ctx.addIssue({
                code: "custom",
                path: ["cells", field],
                message: "Every source declaration requires cell provenance",
            });
    }
    for (const [field, cell] of Object.entries(value.cells)) {
        if (!Object.hasOwn(value.declared, field) ||
            !cell.cell.endsWith(String(value.sourceRow)) ||
            Number(cell.cell.replace(/^[A-Z]+/, "")) !== value.sourceRow)
            ctx.addIssue({
                code: "custom",
                path: ["cells", field],
                message: "Provenance must identify this declared field and exact source row",
            });
    }
});
export const productCatalogManifestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    sourceSha256: hash,
    sourceBytes: z
        .number()
        .int()
        .min(1)
        .max(10 * 1024 * 1024),
    worksheet: z.string().min(1).max(200),
    worksheetPart: z.string().regex(/^xl\/worksheets\/sheet[1-9]\d*\.xml$/),
    headerRow: z.number().int().min(1).max(100),
    rowCount: z.number().int().min(1).max(PRODUCT_CATALOG_LIMITS.maxRows),
    rowsSha256: hash,
    preparedBytes: z.number().int().min(1).max(PRODUCT_CATALOG_LIMITS.maxPreparedBytes),
    sourceObservedOn: date.nullable(),
    preparationVersion: z.literal("product-catalog-xlsx-v1"),
    productImages: z.literal("PENDING"),
    skuNamespace: z.literal("INTERNAL_PRODUCT_CODE"),
})
    .strict();
export const productCatalogCreateSchema = z
    .object({ name: z.string().trim().min(1).max(160) })
    .strict();
export const productCatalogImportCreateSchema = z
    .object({ manifest: productCatalogManifestSchema })
    .strict();
export const productCatalogImportBatchSchema = z
    .object({
    rows: z.array(productCatalogPreparedRowSchema).min(1).max(PRODUCT_CATALOG_LIMITS.maxBatchRows),
})
    .strict();
export const productCatalogActivateSchema = z
    .object({ rowsSha256: hash, reason: z.string().trim().min(10).max(1_000) })
    .strict();
export const productCatalogRevisionSchema = z
    .object({
    id: z.string().uuid(),
    catalogId: z.string().uuid(),
    version: z.number().int().positive(),
    status: z.enum(["DRAFT", "ACTIVE"]),
    manifest: productCatalogManifestSchema,
    receivedRows: z.number().int().nonnegative(),
    receivedBytes: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    activatedAt: z.string().datetime().nullable(),
    gtinConflictGroups: z.number().int().nonnegative(),
    skuConflictGroups: z.number().int().nonnegative(),
})
    .strict();
export const productCatalogSummarySchema = z
    .object({
    id: z.string().uuid(),
    name: z.string(),
    currentRevision: productCatalogRevisionSchema.nullable(),
    createdAt: z.string().datetime(),
})
    .strict();
export const productSupplierDeclarationSchema = z
    .object({
    id: z.string().uuid(),
    profile: z.enum(["INDUSTRY", "DISTRIBUTOR", "UNKNOWN"]),
    status: z.literal("DECLARED"),
    validFrom: date,
    validTo: date.nullable(),
    sourceReference: z.string().min(10).max(1_000),
    createdAt: z.string().datetime(),
})
    .strict();
export const productSupplierSchema = z
    .object({
    id: z.string().uuid(),
    name: z.string(),
    externalId: identifier.nullable(),
    profileDeclaration: productSupplierDeclarationSchema.nullable(),
})
    .strict();
export const productSupplierCreateSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    externalId: identifier.nullable().default(null),
})
    .strict();
export const productSupplierDeclarationCreateSchema = productSupplierDeclarationSchema
    .omit({ id: true, status: true, createdAt: true })
    .superRefine((v, ctx) => {
    if (v.validTo && v.validTo < v.validFrom)
        ctx.addIssue({ code: "custom", path: ["validTo"], message: "End date precedes start date" });
});
export const productCatalogBindingCreateSchema = z
    .object({
    supplierId: z.string().uuid(),
    validFrom: date,
    validTo: date.nullable(),
    sourceReference: z.string().trim().min(10).max(1_000),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.validTo && v.validTo < v.validFrom)
        ctx.addIssue({ code: "custom", path: ["validTo"], message: "End date precedes start date" });
});
export const productCatalogBindingSchema = productCatalogBindingCreateSchema.safeExtend({
    id: z.string().uuid(),
    revisionId: z.string().uuid(),
    catalogId: z.string().uuid(),
    createdAt: z.string().datetime(),
});
export const productClassificationReviewCreateSchema = z
    .object({
    ncm: z.string().regex(/^\d{8}$/),
    cest: z
        .string()
        .regex(/^\d{2}\.\d{3}\.\d{2}$/)
        .nullable(),
    reason: z.string().trim().min(10).max(1_000),
    sourceReference: z.string().trim().min(10).max(1_000),
})
    .strict();
export const productClassificationReviewSchema = productClassificationReviewCreateSchema.extend({
    id: z.string().uuid(),
    status: z.literal("REVIEWED_CLASSIFICATION"),
    reviewedAt: z.string().datetime(),
    reviewerId: z.string().uuid(),
});
export const productCatalogProductSchema = z
    .object({
    id: z.string().uuid(),
    catalogId: z.string().uuid(),
    revisionId: z.string().uuid(),
    skuNamespace: z.literal("INTERNAL_PRODUCT_CODE"),
    sku: identifier.nullable(),
    name: nullableText,
    brand: nullableText,
    gtin: z.string().nullable(),
    ncm: z.string().nullable(),
    cest: z.string().nullable(),
    declared: productCatalogDeclaredSchema,
    classification: z
        .object({
        status: z.enum(["UNREVIEWED", "REVIEWED_CLASSIFICATION"]),
        review: productClassificationReviewSchema.nullable(),
        fiscalRulesQualified: z.literal(false),
    })
        .strict(),
    issues: z.array(productCatalogIssueSchema).max(20),
    image: z.object({ status: z.literal("PENDING"), url: z.null() }).strict(),
    provenance: z
        .object({
        sourceSha256: hash,
        sourceRowSha256: hash,
        worksheet: z.string(),
        worksheetPart: z.string(),
        sourceRow: z.number().int(),
        cells: z.record(z.string(), productCatalogCellSchema),
    })
        .strict(),
})
    .strict();
export const productCatalogProductsQuerySchema = z
    .object({
    q: z.string().trim().max(160).default(""),
    limit: z.coerce.number().int().min(1).max(100).default(40),
    cursor: z.string().min(1).max(4_096).optional(),
    revisionId: z.string().uuid().optional(),
})
    .strict();
export const productCatalogProductsPageSchema = z
    .object({
    revision: productCatalogRevisionSchema.nullable(),
    items: z.array(productCatalogProductSchema).max(100),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
    total: z.number().int().nonnegative(),
})
    .strict();
export const productSupplierProductLinkCreateSchema = z
    .object({
    catalogId: z.string().uuid(),
    revisionId: z.string().uuid(),
    productId: z.string().uuid(),
    supplierSku: identifier,
    validFrom: date,
    validTo: date.nullable(),
    sourceReference: z.string().trim().min(10).max(1_000),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.validTo && v.validTo < v.validFrom)
        ctx.addIssue({ code: "custom", path: ["validTo"], message: "End date precedes start date" });
});
export const productSupplierProductLinkSchema = productSupplierProductLinkCreateSchema.safeExtend({
    id: z.string().uuid(),
    supplierId: z.string().uuid(),
    createdAt: z.string().datetime(),
});
export const productCatalogMatchSchema = z
    .object({
    supplierId: z.string().uuid(),
    supplierSku: identifier,
    on: date,
    linkId: z.string().uuid().optional(),
})
    .strict();
export const productCatalogMatchResultSchema = z
    .object({
    status: z.enum(["SUPPLIER_SKU_MAPPING_REQUIRED", "NOT_FOUND", "AMBIGUOUS", "MATCHED"]),
    fiscalRulesQualified: z.literal(false),
    supplier: productSupplierSchema,
    matches: z
        .array(z
        .object({ link: productSupplierProductLinkSchema, product: productCatalogProductSchema })
        .strict())
        .max(100),
    hasMore: z.boolean(),
})
    .strict();
//# sourceMappingURL=product-catalog.js.map